/*
 * Who is signed in, which company they are working in, and their role there. The company's brand color becomes the
 * app's main color (--brand), so each brand looks like itself.
 */
import { can, OPEN_WHEN_NAMES_PRIVATE, type Action, type Role } from "@steward/shared";
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react";
import type { Company, Me } from "../api/hooks";

interface SessionValue {
  me: Me;
  companies: Company[];
  company: Company;
  role: Role;
  can: (action: Action) => boolean;
  switchCompany: (id: string) => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({
  me,
  companies,
  company,
  onSwitch,
  children,
}: {
  me: Me;
  companies: Company[];
  company: Company;
  onSwitch: (id: string) => void;
  children: ReactNode;
}) {
  const role = (company.role ?? "crew") as Role;
  useEffect(() => {
    document.documentElement.style.setProperty("--brand", company.brandColor ?? "hsl(107, 58%, 33%)");
  }, [company.brandColor]);
  const switchCompany = useCallback((id: string) => onSwitch(id), [onSwitch]);
  const value = useMemo(
    () => ({
      me,
      companies,
      company,
      role,
      // A franchise that keeps its customer names from head office: owners from above get only the business parts.
      can: (a: Action) => can(role, a) && (!company.namesPrivate || OPEN_WHEN_NAMES_PRIVATE.includes(a)),
      switchCompany,
    }),
    [me, companies, company, role, switchCompany],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const s = useContext(SessionContext);
  if (!s) throw new Error("useSession outside the signed-in app");
  return s;
}

/** Crew words in their language (owner screens stay English). */
const CREW_WORDS = {
  en: {
    myDay: "My day",
    home: "Home",
    schedule: "Schedule",
    timeOff: "Time off",
    myDayIntro: "Your jobs, in order. Tap an address to open it in maps.",
    noJobs: "No jobs for you yet",
    noJobsHint: "When the office puts you on a job, it shows here.",
    notes: "Notes for the crew",
    signOut: "Log out",
    security: "My devices",
    language: "Language",
    languageName: "English",
    more: "More",
    oneTime: "One time",
    weekly: "Every week",
    biweekly: "Every 2 weeks",
    monthly: "Every month",
    today: "Today",
    tomorrow: "Tomorrow",
    yesterday: "Yesterday",
    dayBefore: "Day before",
    dayAfter: "Next day",
    noJobsDay: "No jobs this day",
    noJobsDayHint: "Check the next day, or ask the office.",
    with: "With",
    done: "Done",
    started: "Started",
    skipped: "Skipped",
    jobCount: "jobs",
    jobOne: "job",
    pictureAndColor: "Your picture and color",
    pictureWhere: "Shows on the schedule and the team list.",
    notOnTeam: "You are not on this company's team, so you have no circle here to change.",
    addPicture: "Add a picture",
    changePicture: "Change picture",
    takePictureOff: "Take it off",
    yourColor: "Your color",
    ringNote: "With a picture, the color is a thin ring around it.",
    pictureSaved: "Picture saved",
    pictureOff: "Picture taken off",
    notAPicture: "That file is not a picture. Pick a photo.",
    pictureFailed: "That picture didn't save. Try another one.",
  },
  es: {
    myDay: "Mi día",
    home: "Inicio",
    schedule: "Horario",
    timeOff: "Días libres",
    myDayIntro: "Tus trabajos, en orden. Toca una dirección para abrirla en el mapa.",
    noJobs: "Todavía no tienes trabajos",
    noJobsHint: "Cuando la oficina te ponga en un trabajo, aparece aquí.",
    notes: "Notas para el equipo",
    signOut: "Cerrar sesión",
    security: "Mis dispositivos",
    language: "Idioma",
    languageName: "Español",
    more: "Más",
    oneTime: "Una vez",
    weekly: "Cada semana",
    biweekly: "Cada 2 semanas",
    monthly: "Cada mes",
    today: "Hoy",
    tomorrow: "Mañana",
    yesterday: "Ayer",
    dayBefore: "Día anterior",
    dayAfter: "Día siguiente",
    noJobsDay: "No hay trabajos este día",
    noJobsDayHint: "Mira el día siguiente, o pregunta a la oficina.",
    with: "Con",
    done: "Listo",
    started: "Empezado",
    skipped: "Saltado",
    jobCount: "trabajos",
    jobOne: "trabajo",
    pictureAndColor: "Tu foto y color",
    pictureWhere: "Se ve en el horario y en la lista del equipo.",
    notOnTeam: "No estás en el equipo de esta empresa, así que aquí no tienes círculo que cambiar.",
    addPicture: "Poner una foto",
    changePicture: "Cambiar foto",
    takePictureOff: "Quitarla",
    yourColor: "Tu color",
    ringNote: "Con foto, el color es un aro fino alrededor.",
    pictureSaved: "Foto guardada",
    pictureOff: "Foto quitada",
    notAPicture: "Ese archivo no es una foto. Elige una foto.",
    pictureFailed: "Esa foto no se guardó. Prueba otra.",
  },
} as const;
export type CrewWord = keyof (typeof CREW_WORDS)["en"];

export function crewWords(locale: string) {
  return CREW_WORDS[locale === "es" ? "es" : "en"];
}
